import { Link } from "react-router-dom";
import { Calendar, Clock } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO, seoConfig } from "@/hooks/useSEO";
import { generateWebPageSchema, generateBreadcrumbSchema } from "@/lib/jsonld";
import coverAlternative from "@/assets/blog/alternative-email-marketing/couverture.webp";
import coverFidelisation from "@/assets/blog/outils-fidelisation-client/couverture.webp";
import coverProgrammes from "@/assets/blog/programmes-fidelite/couverture.webp";
import coverNotifications from "@/assets/blog/notifications-push-wallet/couverture.webp";

const Blog = () => {
  const jsonLdSchemas = [
    generateWebPageSchema("Blog", seoConfig.blog.description, "/blog"),
    generateBreadcrumbSchema([
      { name: "Accueil", path: "/" },
      { name: "Blog", path: "/blog" }
    ])
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SEO {...seoConfig.blog} jsonLd={jsonLdSchemas} />
      <Header />

      <main className="flex-1">
        {/* Hero Section */}
        <section className="pt-24 lg:pt-40 pb-12 sm:pb-16">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            {/* Badge */}
            <div className="inline-block px-4 py-2 rounded-full border border-[#074155] bg-white text-sm font-semibold text-[#074155] mb-6">
              Ressources
            </div>

            {/* Title H1 */}
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#074155] mb-4">
              Blog <span className="text-[#0c97a9]">Unotif</span>
            </h1>

            {/* Subtitle */}
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] max-w-2xl mx-auto">
              Consultez nos conseils, stratégies et informations concernant le marketing Wallet et les notifications push
            </p>
          </div>
        </section>

        {/* Articles Section */}
        <section className="pb-20 sm:pb-28 lg:pb-36">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">

            {/* Article 4 - Notifications push wallet */}
            <Link to="/notifications-push-wallet-fidelisation" className="block group">
              <article>
                <img 
                  src={coverNotifications}
                  alt="Illustration des notifications push wallet sur smartphone avec écran verrouillé et message marketing visible en 2026"
                  className="w-full h-auto rounded-2xl object-cover mb-6"
                  style={{
                    boxShadow: "0 8px 32px -8px rgba(0,0,0,0.12), 0 4px 16px -4px rgba(0,0,0,0.08)"
                  }}
                />
                
                <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-3 group-hover:text-[#0c97a9] transition-colors">
                  Notifications push wallet : le canal de fidélisation qui cartonne en 2026
                </h2>
                
                <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4 line-clamp-3">
                  Les notifications push wallet révolutionnent la fidélisation client en 2026 : découvrez un canal direct avec fort taux d'ouverture, sans email ni SMS.
                </p>
                
                <div className="flex items-center gap-4 text-sm font-medium text-[#495456]">
                  <span className="flex items-center gap-1.5">
                    <Calendar className="w-4 h-4" />
                    23 mars 2026
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Clock className="w-4 h-4" />
                    12 min
                  </span>
                </div>
              </article>
            </Link>

            {/* Article 3 - Programmes fidélité échouent */}
            <Link to="/programme-fidelite-ne-fonctionne-pas" className="block group">
              <article>
                <img 
                  src={coverProgrammes}
                  alt="Programme de fidélité ne fonctionne pas"
                  className="w-full h-auto rounded-2xl object-cover mb-6"
                  style={{
                    boxShadow: "0 8px 32px -8px rgba(0,0,0,0.12), 0 4px 16px -4px rgba(0,0,0,0.08)"
                  }}
                />
                
                <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-3 group-hover:text-[#0c97a9] transition-colors">
                  Pourquoi 58 % des programmes de fidélité sont abandonnés (et comment éviter les mêmes erreurs)
                </h2>
                
                <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4 line-clamp-3">
                  58 % des membres d'un programme de fidélité ne l'utilisent plus après 6 mois. Découvrez les 5 erreurs qui tuent un programme de fidélité et les correctifs à appliquer.
                </p>
                
                <div className="flex items-center gap-4 text-sm font-medium text-[#495456]">
                  <span className="flex items-center gap-1.5">
                    <Calendar className="w-4 h-4" />
                    18 mars 2026
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Clock className="w-4 h-4" />
                    10 min
                  </span>
                </div>
              </article>
            </Link>

            {/* Article 2 - Outils fidélisation */}
            <Link to="/meilleurs-outils-de-fidelisation-client" className="block group">
              <article>
                <img 
                  src={coverFidelisation}
                  alt="Les meilleurs outils de fidélisation client"
                  className="w-full h-auto rounded-2xl object-cover mb-6"
                  style={{
                    boxShadow: "0 8px 32px -8px rgba(0,0,0,0.12), 0 4px 16px -4px rgba(0,0,0,0.08)"
                  }}
                />
                
                <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-3 group-hover:text-[#0c97a9] transition-colors">
                  Les meilleurs outils de fidélisation client : le guide complet 2026
                </h2>
                
                <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4 line-clamp-3">
                  Acquérir un nouveau client coûte 5 à 7 fois plus cher que d'en fidéliser un existant. Découvrez les 7 outils de fidélisation client les plus efficaces en 2026.
                </p>
                
                <div className="flex items-center gap-4 text-sm font-medium text-[#495456]">
                  <span className="flex items-center gap-1.5">
                    <Calendar className="w-4 h-4" />
                    13 mars 2026
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Clock className="w-4 h-4" />
                    15 min
                  </span>
                </div>
              </article>
            </Link>

            {/* Article 1 - Alternative email */}
            <Link to="/alternative-email-marketing" className="block group">
              <article>
                <img 
                  src={coverAlternative}
                  alt="Alternative à l'email marketing : quelles solutions en 2026 ?"
                  className="w-full h-auto rounded-2xl object-cover mb-6"
                  style={{
                    boxShadow: "0 8px 32px -8px rgba(0,0,0,0.12), 0 4px 16px -4px rgba(0,0,0,0.08)"
                  }}
                />
                
                <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-3 group-hover:text-[#0c97a9] transition-colors">
                  Alternative à l'email marketing : quelles solutions en 2026 ?
                </h2>
                
                <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4 line-clamp-3">
                  Pendant des années, l'email marketing a été le canal roi. Mais en 2026, une réalité s'impose : l'email marketing ne produit plus les mêmes résultats. Découvrez les meilleures alternatives.
                </p>
                
                <div className="flex items-center gap-4 text-sm font-medium text-[#495456]">
                  <span className="flex items-center gap-1.5">
                    <Calendar className="w-4 h-4" />
                    4 février 2026
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Clock className="w-4 h-4" />
                    12 min
                  </span>
                </div>
              </article>
            </Link>

          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Blog;
